import { Close } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import * as React from "react";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { TimeEnum } from "../../enums/TimeEnum";
import PartnerSlotService from "../../services/PartnerTimeSlotService";

interface InputProps {
  open: boolean;
  callback: () => void;
}

const partnerSlotService = new PartnerSlotService();

export const BulkBlockCookSlotsDialog = (props: InputProps) => {
  const [cookIdsInput, setCookIdsInput] = React.useState<string>("");
  const [date, setDate] = React.useState<Date | null>(new Date());
  const [startTime, setStartTime] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("");
  const [adminComment, setAdminComment] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);

  const handleClose = () => {
    setCookIdsInput("");
    setDate(new Date());
    setStartTime("");
    setEndTime("");
    setAdminComment("");
    props.callback();
  };

  const handleSubmit = async () => {
    const rawIds = cookIdsInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (rawIds.length === 0 || !date || !startTime || !endTime) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please fill all required fields",
        type: CustomSnackbarTypes.WARNING,
      });
      return;
    }

    const cookIds = rawIds.map(Number);
    if (cookIds.some(isNaN)) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Cook IDs must be valid numbers",
        type: CustomSnackbarTypes.WARNING,
      });
      return;
    }

    setLoading(true);
    try {
      await partnerSlotService.bulkBlockCookSlots({
        cookIds,
        date: moment(date).format("YYYY-MM-DD"),
        startTime,
        endTime,
        ...(adminComment.trim() ? { adminComment: adminComment.trim() } : {}),
      });
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: `Bulk block task queued for ${cookIds.length} cook(s)`,
        type: CustomSnackbarTypes.SUCCESS,
      });
      handleClose();
    } catch {
      // Error snackbar shown by HttpClient interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onClose={handleClose} open={props.open} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Block Cook Slots</DialogTitle>
      <IconButton onClick={handleClose} sx={{ position: "absolute", top: 8, right: 8 }}>
        <Close />
      </IconButton>
      <DialogContent>
        <Grid container columns={1} rowSpacing={2} style={{ padding: "8px 0" }}>
          <Grid item xs={1} style={{ width: "100%" }}>
            <TextField
              fullWidth
              label="Cook IDs (comma separated)"
              placeholder="e.g. 101, 202, 303"
              value={cookIdsInput}
              onChange={(e) => setCookIdsInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={1} style={{ width: "100%", marginTop: "16px" }}>
            <FormControl fullWidth>
              <DatePicker
                label="Date"
                inputFormat="dd MMM yyyy"
                disableMaskedInput
                value={date}
                onChange={(value) => setDate(value)}
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>
          </Grid>
          <Grid item xs={1} style={{ width: "100%", marginTop: "16px" }}>
            <FormControl fullWidth>
              <InputLabel id="bulk-start-time-label">Start Time</InputLabel>
              <Select
                labelId="bulk-start-time-label"
                value={startTime}
                label="Start Time"
                onChange={(e) => setStartTime(e.target.value)}
              >
                {Object.values(TimeEnum).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} style={{ width: "100%", marginTop: "16px" }}>
            <FormControl fullWidth>
              <InputLabel id="bulk-end-time-label">End Time</InputLabel>
              <Select
                labelId="bulk-end-time-label"
                value={endTime}
                label="End Time"
                onChange={(e) => setEndTime(e.target.value)}
              >
                {Object.values(TimeEnum).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} style={{ width: "100%", marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Admin Comment (optional)"
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ marginTop: "8px" }}>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Submit"}
        </Button>
        <Button variant="contained" color="error" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
